import {
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { DatePipe, isPlatformBrowser } from '@angular/common';
import { ChartModule } from 'primeng/chart';

import { CardHeaderComponent } from './card-header.component';

interface WeekDay {
  label: string;   // 'Mon'
  date: string;    // 'YYYY-MM-DD'
  hours: string;   // '8:12' or '—'
  minutes: number; // for the weekly total + bar height
  isToday: boolean;
}

/** Full shift length in minutes — drives the progress ring (dummy: 8h). */
const SHIFT_MINUTES = 8 * 60;
/** Ring geometry: r=52 → circumference for stroke-dashoffset. */
const RING_CIRCUMFERENCE = 2 * Math.PI * 52;

/**
 * Read-only "today + this week" attendance card. Punching lives in the top bar;
 * this card only reflects it. The ring fills with today's worked time against the
 * shift length and ticks live off the open session in localStorage
 * ('punch_session', written by the top-bar punch clock). This week is a bar chart.
 */
@Component({
  selector: 'app-attendance-clock-tile',
  standalone: true,
  imports: [CardHeaderComponent, DatePipe, ChartModule],
  template: `
    <div
      class="card flex h-full min-h-[24rem] flex-col rounded-xl border border-surface-200/60 bg-surface-0 p-5 dark:border-surface-700/40 dark:bg-surface-900"
    >
      <app-card-header
        [title]="title()"
        [subtitle]="subtitle()"
        [icon]="icon()"
        [accent]="accent()"
      />

      <div class="mt-4 flex flex-1 flex-col gap-6 sm:flex-row sm:items-stretch">
        <!-- Shift progress ring + today's running total -->
        <div class="flex flex-col items-center justify-center gap-2 sm:flex-1">
          <div class="text-xs uppercase tracking-wider text-text-muted">
            {{ now() | date: 'EEEE, d MMM' }}
          </div>
          <div class="relative h-36 w-36">
            <svg viewBox="0 0 120 120" class="h-full w-full -rotate-90">
              <circle
                cx="60" cy="60" r="52" fill="none" stroke-width="8"
                class="stroke-surface-100 dark:stroke-surface-800"
              />
              <circle
                cx="60" cy="60" r="52" fill="none" stroke-width="8" stroke-linecap="round"
                class="stroke-primary-500 transition-[stroke-dashoffset] duration-500"
                [attr.stroke-dasharray]="circumference"
                [attr.stroke-dashoffset]="ringOffset()"
              />
            </svg>
            <div class="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
              <span class="text-[10px] uppercase tracking-wider text-text-muted">Today</span>
              <span class="text-2xl font-semibold tabular-nums tracking-tight text-text-main">
                {{ todayTotal() }}
              </span>
              <span class="text-[10px] text-text-muted">of {{ shiftLabel }}</span>
            </div>
          </div>

          <!-- Punch in / out times for today -->
          <div class="mt-1 flex w-full items-stretch gap-2">
            <div class="flex flex-1 flex-col items-center rounded-lg bg-surface-50 py-1.5 dark:bg-surface-800/50">
              <span class="text-[10px] uppercase tracking-wider text-text-muted">In</span>
              <span class="text-sm font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                {{ punchIn() }}
              </span>
            </div>
            <div class="flex flex-1 flex-col items-center rounded-lg bg-surface-50 py-1.5 dark:bg-surface-800/50">
              <span class="text-[10px] uppercase tracking-wider text-text-muted">Out</span>
              <span class="text-sm font-semibold tabular-nums text-indigo-600 dark:text-indigo-400">
                {{ punchOut() }}
              </span>
            </div>
          </div>
        </div>

        <!-- This week — line chart (matches the KPI tiles) -->
        <div class="flex w-full flex-col sm:w-1/2">
          <div class="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
            This Week
          </div>

          @if (loading()) {
            <div class="flex-1 animate-pulse rounded-lg bg-surface-100 dark:bg-surface-800"></div>
          } @else {
            <div class="min-h-0 flex-1">
              <p-chart type="line" [data]="chartData()" [options]="chartOptions" [style]="{ height: '7rem' }" />
            </div>
            <div
              class="mt-2 flex items-center justify-between border-t border-surface-200 pt-2 dark:border-surface-700"
            >
              <span class="text-sm font-semibold text-text-main">Weekly total</span>
              <span class="tabular-nums text-base font-bold text-text-main">{{ weekTotal() }}</span>
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class AttendanceClockTileComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);

  title = input.required<string>();
  subtitle = input<string>('');
  icon = input<string>('pi pi-clock');
  accent = input<string>('emerald');

  readonly circumference = RING_CIRCUMFERENCE;
  readonly shiftLabel = `${Math.floor(SHIFT_MINUTES / 60)}h`;

  now = signal<Date>(new Date());
  loading = signal(true);
  week = signal<WeekDay[]>([]);
  weekTotal = signal('0:00');
  todayTotal = signal('00:00:00');
  ringOffset = signal(RING_CIRCUMFERENCE);
  punchIn = signal('—');
  punchOut = signal('—');

  private clockRef?: ReturnType<typeof setInterval>;
  private todayRef?: ReturnType<typeof setInterval>;
  /** Today's already-recorded minutes (closed sessions), before any live one. */
  private recordedTodayMin = 0;

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.clockRef = setInterval(() => this.now.set(new Date()), 1000);
    this.loadWeek();
    this.startTodayTicker();
  }

  ngOnDestroy(): void {
    clearInterval(this.clockRef);
    clearInterval(this.todayRef);
  }

  /** Line chart of hours worked per day this week — matches the KPI tiles. */
  chartData = computed(() => {
    const days = this.week();
    const hue = '#3b82f6';
    return {
      labels: days.map((d) => d.label),
      datasets: [
        {
          label: 'Hours',
          data: days.map((d) => Math.round((d.minutes / 60) * 100) / 100),
          borderColor: hue,
          backgroundColor: hue + '22',
          borderWidth: 2,
          fill: true,
          tension: 0.35,
          pointRadius: days.map((d) => (d.isToday ? 4 : 2)),
          pointBackgroundColor: hue,
        },
      ],
    };
  });

  readonly chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (c: { parsed: { y: number } }) => `${c.parsed.y} h` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 10 } } },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(148, 163, 184, 0.18)' },
        ticks: { color: '#94a3b8', font: { size: 10 }, maxTicksLimit: 4 },
      },
    },
  };

  // ── This week ──────────────────────────────────────────────────────────────

  private loadWeek(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Monday as week start.
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));

    const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    // DUMMY DATA — sample worked-minutes per weekday (Mon–Sun). Swap loadWeek()
    // back to getAttendanceLogs() to show real attendance.
    const dummyMinutes = [492, 483, 478, 500, 470, 0, 0];
    const todayStr = this.fmt(today);

    const days: WeekDay[] = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const date = this.fmt(d);
      const isToday = date === todayStr;
      // Today only counts what's been recorded so far, not a full dummy day.
      const mins = isToday ? 0 : dummyMinutes[i];
      if (isToday) this.recordedTodayMin = mins;
      return {
        label: labels[i],
        date,
        hours: mins > 0 ? this.hm(mins) : '—',
        minutes: mins,
        isToday,
      };
    });

    this.week.set(days);
    this.recomputeTotals();
    this.loading.set(false);
  }

  private recomputeTotals(): void {
    const total = this.week().reduce((s, d) => s + d.minutes, 0);
    this.weekTotal.set(this.hm(total));
  }

  // ── Today's live total + ring ──────────────────────────────────────────────

  private startTodayTicker(): void {
    const tick = () => {
      const openMin = this.openSessionMinutes();
      const totalSec = Math.round((this.recordedTodayMin + openMin) * 60) + this.openSessionExtraSeconds();
      this.todayTotal.set(this.hms(totalSec));

      // Ring fills with worked time against the shift, capped at a full circle.
      const frac = Math.min(1, totalSec / (SHIFT_MINUTES * 60));
      this.ringOffset.set(RING_CIRCUMFERENCE * (1 - frac));

      this.refreshPunchTimes();
    };
    tick();
    this.todayRef = setInterval(tick, 1000);
  }

  /**
   * Today's punch in/out for display. The live session ('punch_session') wins for
   * the in-time while on shift; otherwise today's stamped record ('punch_today',
   * written by the top-bar punch clock) supplies both in and out.
   */
  private refreshPunchTimes(): void {
    const fmt = (iso?: string | null): string => {
      if (!iso) return '—';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };
    try {
      const session = localStorage.getItem('punch_session');
      const today = JSON.parse(localStorage.getItem('punch_today') ?? '{}');
      const isToday = today?.date === this.fmt(new Date());

      if (session) {
        this.punchIn.set(fmt(JSON.parse(session).clockInTime));
        this.punchOut.set('—'); // still on shift
      } else if (isToday) {
        this.punchIn.set(fmt(today.clockIn));
        this.punchOut.set(fmt(today.clockOut));
      } else {
        this.punchIn.set('—');
        this.punchOut.set('—');
      }
    } catch {
      this.punchIn.set('—');
      this.punchOut.set('—');
    }
  }

  /** Whole minutes elapsed in the currently open punch session, if any. */
  private openSessionMinutes(): number {
    const start = this.sessionStart();
    if (!start) return 0;
    return Math.floor((Date.now() - start) / 60000);
  }

  private openSessionExtraSeconds(): number {
    const start = this.sessionStart();
    if (!start) return 0;
    return Math.floor(((Date.now() - start) % 60000) / 1000);
  }

  private sessionStart(): number | null {
    try {
      const saved = localStorage.getItem('punch_session');
      if (!saved) return null;
      const t = new Date(JSON.parse(saved).clockInTime).getTime();
      return isNaN(t) ? null : t;
    } catch {
      return null;
    }
  }

  // ── Formatting ─────────────────────────────────────────────────────────────

  private fmt(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  /** minutes → 'H:MM' */
  private hm(min: number): string {
    return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
  }

  /** seconds → 'HH:MM:SS' */
  private hms(sec: number): string {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
}
